import React, { forwardRef } from 'react';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import {
  FilterToken,
  GridChevron,
  GridFiltersButton,
  GridSearch,
} from '../../components/data-grid';
import { getTruckTitle } from './truck-assignments.utils';

const DateButton = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    className="agv-dg-ghost-btn"
    onClick={onClick}
    ref={ref}
  >
    <span className="agv-dg-ghost-btn__key">Date :</span>
    <span className="agv-dg-ghost-btn__value">{value}</span>
    <GridChevron />
  </button>
));
DateButton.displayName = 'DateButton';

/**
 * Barre de filtres, alignée sur celle du puits de commandes : recherche,
 * filtres avancés (camion, camions vides) et leurs jetons, date à droite
 */
export default function FiltersBar({
  search,
  onSearchChange,
  selectedDate,
  onDateChange,
  calendarDays,
  onCalendarMonthChange,
  trucks,
  truckCounts,
  selectedVehicleIds,
  onVehicleIdsChange,
  onResetFilters,
  hideEmptyTrucks,
  onHideEmptyTrucksChange,
}) {
  const activeFiltersCount =
    (selectedVehicleIds.size > 0 ? 1 : 0) + (hideEmptyTrucks ? 1 : 0);

  const toggleVehicle = (vehicleId, checked) => {
    const next = new Set(selectedVehicleIds);
    if (checked) next.add(vehicleId);
    else next.delete(vehicleId);
    onVehicleIdsChange(next);
  };

  const selectedTrucksLabel = trucks
    .filter((truck) => selectedVehicleIds.has(truck.id))
    .map((truck) => truck.key)
    .join(', ');

  return (
    <div className="agv-dg-toolbar ta-filters">
      <div className="agv-dg-toolbar__filters">
        <GridSearch
          value={search}
          onChange={onSearchChange}
          placeholder="Rechercher un BL, un réceptionnaire, une ville…"
        />

        <GridFiltersButton activeCount={activeFiltersCount} width={300}>
          {() => (
            <div className="agv-dg-popover">
              <div className="agv-dg-popover__field">
                <span className="agv-dg-popover__label">Camion</span>
                <label className="ta-popover__option">
                  <input
                    type="checkbox"
                    checked={selectedVehicleIds.size === 0}
                    onChange={() => onVehicleIdsChange(new Set())}
                  />
                  Tous les camions
                </label>
                <div className="ta-filters__trucks">
                  {trucks.map((truck) => {
                    // Un camion sans commande ce jour n'est pas filtrable
                    const count = truckCounts[truck.id] || 0;
                    return (
                      <label
                        key={truck.id}
                        className={`ta-popover__option ${
                          count === 0 ? 'ta-popover__option--disabled' : ''
                        }`}
                        title={
                          count === 0 ? 'Aucune commande ce jour' : undefined
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selectedVehicleIds.has(truck.id)}
                          disabled={count === 0}
                          onChange={(e) =>
                            toggleVehicle(truck.id, e.target.checked)
                          }
                        />
                        <span
                          className="ta-dot"
                          style={{ backgroundColor: truck.color }}
                        />
                        {getTruckTitle(truck)}
                        <span className="ta-spacer" />
                        <span className="ta-popover__count">{count}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="agv-dg-popover__field">
                <span className="agv-dg-popover__label">Affichage</span>
                <label className="ta-popover__option">
                  <input
                    type="checkbox"
                    checked={hideEmptyTrucks}
                    onChange={(e) => onHideEmptyTrucksChange(e.target.checked)}
                  />
                  Masquer les camions sans commande
                </label>
              </div>
            </div>
          )}
        </GridFiltersButton>

        {activeFiltersCount > 0 && (
          <span className="agv-dg-toolbar__separator" />
        )}
        {selectedVehicleIds.size > 0 && (
          <FilterToken
            label="Camion"
            value={selectedTrucksLabel}
            onRemove={() => onVehicleIdsChange(new Set())}
          />
        )}
        {hideEmptyTrucks && (
          <FilterToken
            label="Affichage"
            value="camions vides masqués"
            onRemove={() => onHideEmptyTrucksChange(false)}
          />
        )}
        {(activeFiltersCount > 0 || search) && (
          <button
            type="button"
            className="agv-dg-link"
            onClick={onResetFilters}
          >
            Effacer
          </button>
        )}
      </div>

      <div className="agv-dg-toolbar__controls">
        <ReactDatePicker
          selected={selectedDate}
          onChange={(date) => date && onDateChange(date)}
          onMonthChange={onCalendarMonthChange}
          onCalendarOpen={() => onCalendarMonthChange(selectedDate)}
          renderDayContents={(dayOfMonth, date) => {
            // Pastille : seulement s'il reste des commandes à placer ce jour
            const day = calendarDays.get(moment(date).format('YYYY-MM-DD'));
            const hasUnplaced = day?.unplaced_count > 0;
            return (
              <span
                className="ta-calendar-day"
                title={
                  day
                    ? [
                        day.unplaced_count > 0 &&
                          `${day.unplaced_count} à placer`,
                        day.trucks_count > 0 &&
                          `${day.trucks_count} camion${
                            day.trucks_count > 1 ? 's' : ''
                          } planifié${day.trucks_count > 1 ? 's' : ''}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : undefined
                }
              >
                {dayOfMonth}
                {hasUnplaced && <span className="ta-calendar-day__dot" />}
              </span>
            );
          }}
          customInput={<DateButton />}
          dateFormat="EEE d MMM yyyy"
          locale="fr"
          popperPlacement="bottom-end"
        />
      </div>
    </div>
  );
}
